import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { color } from "d3";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function SimpleGraphLine({ data }) {
    const [chartData, setChartData] = useState(null);
    const chartRef = useRef(null);
    const { theme } = useTheme();
    const { t } = useTranslation();

    const verticalLinePlugin = {
        id: 'verticalLine',
        beforeDraw: (chart) => {
            const tooltip = chart.tooltip;
            if (tooltip && tooltip._active && tooltip._active.length) {
                const ctx = chart.ctx;
                const activePoint = tooltip._active[0];

                const x = activePoint.element.x;
                const yStart = activePoint.element.y;
                const yEnd = chart.chartArea.bottom;

                ctx.save();
                ctx.beginPath();
                ctx.moveTo(x, yStart);
                ctx.lineTo(x, yEnd);
                ctx.lineWidth = 1;
                ctx.strokeStyle = theme === "dark" ? '#997adb' : '#124188';
                ctx.setLineDash([4, 4]);
                ctx.stroke();
                ctx.restore();
            }
        }
    };

    const shortMonthNames = {
        "01": t("Janv"),
        "02": t("Fév"),
        "03": t("Mars"),
        "04": t("Avril"),
        "05": t("Mai"),
        "06": t("Juin"),
        "07": t("Juil"),
        "08": t("Août"),
        "09": t("Sept"),
        "10": t("Oct"),
        "11": t("Nov"),
        "12": t("Déc")
    };

    const fullMonthNames = {
        "01": t("Janvier"),
        "02": t("Février"),
        "03": t("Mars"),
        "04": t("Avril"),
        "05": t("Mai"),
        "06": t("Juin"),
        "07": t("Juillet"),
        "08": t("Août"),
        "09": t("Septembre"),
        "10": t("Octobre"),
        "11": t("Novembre"),
        "12": t("Décembre")
    };

    const shortDayNames = {
        "Mon": t("Lun"),
        "Tue": t("Mar"),
        "Wed": t("Mer"),
        "Thu": t("Jeu"),
        "Fri": t("Ven"),
        "Sat": t("Sam"),
        "Sun": t("Dim")
    };

    useEffect(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return;
        }

        const months = data.map(item => {
            if (item && item.day) {
                const dateObj = new Date(item.day + 'T00:00:00');
                const dayAbbr = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                return shortDayNames[dayAbbr] || dayAbbr;
            }
            if (item && item.month) {
                const [year, month] = item.month.split('-');
                return `${shortMonthNames[month]}`;
            }
            return '';
        });

        const ticketCounts = data.map(item => {
            if (item && item.ticket_count) {
                return parseInt(item.ticket_count, 10);
            }
            return 0;
        });

        setChartData({
            labels: months,
            datasets: [
                {
                    label: t("Nombre d'incidents"),
                    data: ticketCounts,
                    borderColor: theme === "dark" ? '#997adb' : '#124188',
                    borderWidth: 3,
                    backgroundColor: (context) => {
                        const chart = context.chart;
                        const { ctx, chartArea } = chart;
                        if (!chartArea) {
                            return null;
                        }
                        const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                        gradient.addColorStop(0, theme === "dark" ? 'rgba(104, 104, 104, 0)' : 'rgba(255, 255, 255, 0)');
                        gradient.addColorStop(1, theme === "dark" ? 'rgb(202, 123, 255)' : 'rgba(18, 65, 136, 0.15)');
                        return gradient;
                    },
                    fill: true,
                    tension: 0.4,
                    pointRadius: 0,
                    pointHoverRadius: 7,
                    pointHoverBackgroundColor: theme === "dark" ? 'rgba(153, 122, 219, 0.87)' : 'rgba(18, 65, 136, 0.87)',
                    pointHoverBorderColor: 'white',
                    pointHoverBorderWidth: 2,
                },
            ],
        });
    }, [data]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: theme === "dark" ? '#ffffff' : '#585858',
                },
            },
            y: {
                grid: {
                    display: true,
                },
                ticks: {
                    color: theme === "dark" ? '#ffffff' : '#585858',
                    maxTicksLimit: 4,
                },
                border: {
                    display: false,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                displayColors: false,
                backgroundColor: "white",
                titleColor: "rgb(60, 60, 67, 0.6)",
                titleFont: { weight: 'normal', size: 12 },
                bodyColor: "#11263C",
                bodyFont: { size: 22, weight: "bold" },
                footerColor: "rgb(60, 60, 67, 0.6)",
                footerFont: { weight: 'normal', size: 12 },
                borderWidth: 1,
                borderColor: "rgb(60, 60, 67, 0.1)",
                bodyAlign: 'left',
                footerSpacing: 4,
                bodySpacing: 6,
                caretPadding: 9,
                caretSize: 10,
                padding: { left: 10, right: 20, top: 7, bottom: 7 },
                cornerRadius: 15,
                callbacks: {
                    title: (tooltipItems) => {
                        return t("Nombre d'incidents");
                    },
                    label: (tooltipItem) => {
                        return `${tooltipItem.raw}`;
                    },
                    footer: (tooltipItems) => {
                        const dataIndex = tooltipItems[0].dataIndex;
                        if (data && data[dataIndex]) {
                            const item = data[dataIndex];
                            if (item.day) {
                                const [year, month, day] = item.day.split('-');
                                const monthName = fullMonthNames[month];
                                return `${day} ${monthName} ${year}`;
                            }
                            if (item.month) {
                                const [year, month] = data[dataIndex].month.split('-');
                                const monthName = fullMonthNames[month];
                                return `${monthName} ${year}`;
                            }
                        }
                        return '';
                    },
                }
            },
            verticalLine: {
                draw: true,
            },
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
    };

    useEffect(() => {
        const handleResize = () => {
            if (chartRef.current) {
                chartRef.current.resize();
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <>
            {chartData && (
                <div className="dashboard-graph-line-simple">
                    <Line ref={chartRef} data={chartData} options={options} plugins={[verticalLinePlugin]} />
                </div>
            )}
        </>
    );
}
